import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { EntityLink } from "Shared/EntityLink";
import {
  type InformationSection,
  InformationView,
} from "Shared/InformationView";
import { formatDate } from "Shared/Utils/Format/date";

interface ProjectInformationProps {
  industry: string;
  clientName: string | null;
  clientID?: string | null;
  language: string | null;
  licensedCountries: string[];
  jobsLimit: number;
  currentJobs: number;
  startDate: string | null;
  endDate: string | null;
  onEdit?: () => void;
  isEditable?: boolean;
}

export const ProjectInformation = ({
  industry,
  clientName,
  clientID,
  language,
  licensedCountries,
  jobsLimit,
  currentJobs,
  startDate,
  endDate,
  onEdit,
  isEditable = true,
}: ProjectInformationProps): React.ReactNode => {
  const { t } = useResource();

  const clientValue =
    clientID && clientName ? (
      <EntityLink
        label={clientName}
        path={AppRouting.ClientDetail.path(clientID)}
      />
    ) : (
      (clientName ?? "-")
    );

  const sections: InformationSection[] = [
    {
      items: [
        {
          label: t(Resources.Projects.ProjectDetail.Industry),
          value: industry,
        },
        {
          label: t(Resources.Projects.ProjectDetail.Client),
          value: clientValue,
        },
        {
          label: t(Resources.Projects.ProjectDetail.Language),
          value: language ?? "-",
        },
        {
          label: t(Resources.Projects.ProjectDetail.LicenceForStates),
          value: licensedCountries.join(", "),
        },
        {
          label: t(Resources.Projects.ProjectDetail.JobsLimit),
          value: jobsLimit,
        },
        {
          label: t(Resources.Projects.ProjectDetail.CurrentJobs),
          value: currentJobs,
        },
        {
          label: t(Resources.Projects.ProjectDetail.StartDate),
          value: formatDate(startDate, undefined, "-"),
        },
        {
          label: t(Resources.Projects.ProjectDetail.EndDate),
          value: formatDate(endDate, undefined, "-"),
        },
      ],
    },
  ];

  return (
    <InformationView
      title={t(Resources.Projects.ProjectDetail.Information)}
      editLabel={t(Resources.Projects.ProjectDetail.EditInformation)}
      onEdit={isEditable ? onEdit : undefined}
      sections={sections}
    />
  );
};
